<template>
  <div class="min-h-screen bg-[#f9fafb] flex flex-col justify-between">
    <!-- Header -->
    <div class="flex gap-2 items-center p-4">
      <button
        class="text-gray-700 text-xl"
        @click="goBack"
      >
        <i class="fas fa-arrow-left" />
      </button>
      <div class="w-full">
        <div class="w-full bg-gray-200 h-2 rounded-full">
          <div
            class="h-2 bg-blue-500 rounded-full"
            :style="{ width: progress + '%' }"
          />
        </div>
      </div>
      <div class="flex items-center text-red-500 font-semibold">
        <i class="fas fa-heart mr-1" /> <span>5</span>
      </div>
    </div>

    <!-- Timer -->
    <div class="px-4 text-sm text-gray-600 mb-2 text-right">
      ⏱ {{ formattedTimer }}
    </div>

    <!-- Questions (paged by batch) -->
    <div class="px-4 mt-6 flex flex-col gap-6">
      <div
        v-for="(question, qIndex) in paginatedQuestions"
        :key="question.questionId"
        class="bg-white rounded-xl shadow p-6"
      >
        <div
          class="text-[16px] text-gray-900 leading-relaxed mb-4"
          v-html="question.question"
        />
        <div
          v-if="question.questionImagePath"
          class="flex justify-center mb-4"
        >
          <img
            :src="question.questionImagePath"
            alt="question image"
            class="max-h-48"
          >
        </div>

        <!-- Options -->
        <div class="flex flex-col gap-3">
          <button
            v-for="(opt, i) in question.answer"
            :key="i"
            :class="[
              'w-full text-left px-4 py-3 rounded-lg border transition font-medium flex items-center gap-3',
              selectedAnswers[batchStartIndex + qIndex]?.Answer === opt.Answers
                ? 'border-blue-500 text-blue-600 bg-blue-50'
                : 'border-gray-300 bg-white text-gray-800',
            ]"
            @click="selectAnswer(batchStartIndex + qIndex, i)"
          >
            <!-- PERFECT-CIRCLE BULLET -->
            <span
              class="answer-bullet border"
              :class="
                selectedAnswers[batchStartIndex + qIndex]?.Answer ===
                  opt.Answers
                  ? 'answer-bullet--on border-blue-600'
                  : 'border-gray-400 bg-white'
              "
            >
              <i
                v-if="
                  selectedAnswers[batchStartIndex + qIndex]?.Answer ===
                    opt.Answers
                "
                class="fas fa-check text-[10px]"
              />
            </span>

            <span class="block">{{ opt.Answers }}</span>
          </button>
        </div>
        <CautionDrawer />
      </div>
    </div>

    <!-- Bottom -->
    <div class="px-4 py-6 border-t mt-6 flex justify-between items-center">
      <div class="text-blue-600 text-sm font-medium flex items-center gap-1">
        <i class="fas fa-check-circle" /> {{ totalAnswered }} of
        {{ questions.length }} answered
      </div>
      <button
        class="bg-blue-600 text-white rounded-full px-8 py-3 text-sm font-semibold"
        :disabled="!canProceed"
        :class="{ 'opacity-50 cursor-not-allowed': !canProceed }"
        @click="goNextBatch"
      >
        {{ isLastBatch ? "Finish" : "Next" }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import { useAuthStore } from "@/stores/authStore";
import { GetProfile, submitAnswer } from "@/api/settingApi";
import { useSnackbarStore } from "@/stores/snackbarStore";
import { getKiddoBaseUrl } from "@/api/httpClient";
import CautionDrawer from "@/components/CautionDrawer.vue";

const snackbar = useSnackbarStore();
const auth = useAuthStore();
const profile = ref({});

const route = useRoute();
const router = useRouter();
const quizId = route.params.id;
const routeRequireReviewPin = computed(() =>
  ["1", "true", "yes"].includes(
    String(route.query.requireReviewPin ?? "").toLowerCase()
  )
);
const handleGetProfile = async () => {
  try {
    const userId = auth.userId;
    const response = await GetProfile(userId);
    profile.value = response || {};
  } catch (error) {
    console.error(error);
    profile.value = {};
  }
};
/* ---------- localStorage keys isolated per quiz ---------- */
const ANSWERS_KEY = `quiz:${quizId}:answers`;
const TIMER_KEY = `quiz:${quizId}:timer`;
const DEFAULT_SECONDS = 600;

/* ---------- state ---------- */
const questions = ref([]);
const selectedAnswers = ref([]);
const batchSize = 5;
const batchIndex = ref(0);
const timer = ref(parseInt(localStorage.getItem(TIMER_KEY)) || DEFAULT_SECONDS);
let timerInterval = null;

/* ---------- computed ---------- */
const batchStartIndex = computed(() => batchIndex.value * batchSize);
const paginatedQuestions = computed(() =>
  questions.value.slice(
    batchStartIndex.value,
    batchStartIndex.value + batchSize
  )
);
const isLastBatch = computed(
  () => (batchIndex.value + 1) * batchSize >= questions.value.length
);
const totalAnswered = computed(
  () => selectedAnswers.value.filter((a) => a != null).length
);
const progress = computed(() =>
  questions.value.length
    ? Math.round((totalAnswered.value / questions.value.length) * 100)
    : 0
);
const canProceed = computed(
  () => !isLastBatch.value || totalAnswered.value === questions.value.length
);
const formattedTimer = computed(() => {
  const min = Math.floor(timer.value / 60);
  const sec = timer.value % 60;
  return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
});

/* ---------- helpers ---------- */
function loadSavedAnswers(list) {
  const fresh = Array(list.length).fill(null);
  let saved;
  try {
    saved = JSON.parse(localStorage.getItem(ANSWERS_KEY) || "[]");
  } catch {
    saved = [];
  }
  if (Array.isArray(saved) && saved.length) {
    for (let i = 0; i < Math.min(saved.length, list.length); i++) {
      const item = saved[i];
      if (item && item.QuestionId === list[i].questionId) fresh[i] = item;
    }
  }
  selectedAnswers.value = fresh;
}
function persistAnswers() {
  localStorage.setItem(ANSWERS_KEY, JSON.stringify(selectedAnswers.value));
}
function clearStorageForThisQuiz() {
  localStorage.removeItem(ANSWERS_KEY);
  localStorage.removeItem(TIMER_KEY);
}
const FALLBACK_SECONDS = 1800;

function parseHmsToSeconds(hmsString) {
  if (!hmsString || typeof hmsString !== "string") return FALLBACK_SECONDS;

  const parts = hmsString.split(":").map(Number);

  if (parts.length !== 3) {
    console.warn(`Invalid H:M:S format: ${hmsString}`);
    return FALLBACK_SECONDS;
  }

  const [hours, minutes, seconds] = parts;

  const totalSeconds = hours * 3600 + minutes * 60 + seconds;

  if (totalSeconds > 0) {
    console.log(`Parsed H:M:S duration: ${totalSeconds} seconds`);
    return totalSeconds;
  }
  return FALLBACK_SECONDS;
}

const truthyStrings = new Set(["1", "true", "yes", "y"]);
const coerceBooleanFlag = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") return truthyStrings.has(value.toLowerCase());
  return false;
};

const reviewPinCandidateKeys = [
  "requireReviewPin",
  "RequireReviewPin",
  "requiresPin",
  "RequiresPin",
  "requirePin",
  "RequirePin",
  "needPin",
  "NeedPin",
  "isNeedPin",
  "IsNeedPin",
  "isValidatePin",
  "IsValidatePin",
  "validatePin",
  "ValidatePin",
  "isPinRequired",
  "IsPinRequired",
  "isPinValidation",
  "IsPinValidation",
  "reviewPinRequired",
  "ReviewPinRequired",
  "pinValidation",
  "PinValidation",
];

const extractPinFlag = (source) => {
  if (!source || typeof source !== "object") return undefined;
  for (const key of reviewPinCandidateKeys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      return coerceBooleanFlag(source[key]);
    }
  }
  return undefined;
};

const inferReviewPinRequirement = (payload) =>
  extractPinFlag(payload) ??
  extractPinFlag(payload?.data) ??
  extractPinFlag(payload?.result) ??
  extractPinFlag(payload?.response);

/* ---------- actions ---------- */
function selectAnswer(index, choiceIndex) {
  const q = questions.value[index];
  const choice = q?.answer?.[choiceIndex];
  if (!q || !choice) return;

  selectedAnswers.value[index] = {
    QuestionId: q.questionId,
    Choice: choice.Value,
    Answer: choice.Answers,
  };
  persistAnswers();
}

async function goNextBatch() {
  if (!isLastBatch.value) {
    batchIndex.value++;
    return;
  }
  clearInterval(timerInterval);

  const payload = JSON.parse(localStorage.getItem(ANSWERS_KEY) || "[]");
  const result = await submitAnswer(quizId, profile.value.studentId, payload);

  if (result) {
    snackbar.trigger("Quiz Done!", "success");
    clearStorageForThisQuiz();

    const sid = result.startId ?? result.StartId;
    const inferred = inferReviewPinRequirement(result);
    const requiresPinReview =
      typeof inferred === "boolean" ? inferred : routeRequireReviewPin.value;
    if (sid) {
      router.push({
        path: `/quiz-finish`,
        query: {
          score: result.Score,
          grade: result.Grade,
          exp: result.Exp,
          startId: sid,
          quizGuid: quizId,
          ...(route.query.quizTitle ? { quizTitle: route.query.quizTitle } : {}),
          ...(requiresPinReview ? { requireReviewPin: "1" } : {}),
        },
      });
    } else {
      router.push({
        path: `/quiz-finish`,
        query: {
          quizGuid: quizId,
          ...(route.query.quizTitle ? { quizTitle: route.query.quizTitle } : {}),
          ...(requiresPinReview ? { requireReviewPin: "1" } : {}),
        },
      });
    }
  } else {
    snackbar.trigger("Quiz isn't done!", "error");
  }
}

function goBack() {
  router.back();
}

onMounted(async () => {
  const baseURL = await getKiddoBaseUrl();

  try {
    const { data } = await axios.get(
      `${baseURL}/read/GetQuizQuestion/${quizId}`
    );
    questions.value = data;
    loadSavedAnswers(data);
  } catch (err) {
    console.error("Gagal mengambil soal:", err);
  }

  const quizDurationHms = route.query.quizDurationHms;
  let initialTimer = FALLBACK_SECONDS;

  const savedTimer = parseInt(localStorage.getItem(TIMER_KEY));
  const hasExistingSession = savedTimer > 0;

  if (hasExistingSession) {
    initialTimer = savedTimer;
  } else if (quizDurationHms) {
    initialTimer = parseHmsToSeconds(quizDurationHms);
  } else {
    console.warn(
      "⚠️ Tidak ada timer di URL/localStorage, gunakan fallback:",
      FALLBACK_SECONDS
    );
  }

  timer.value = initialTimer;
  localStorage.setItem(TIMER_KEY, String(timer.value));

  timerInterval = setInterval(async () => {
    if (timer.value > 0) {
      timer.value--;
      localStorage.setItem(TIMER_KEY, String(timer.value));
    } else {
      clearInterval(timerInterval);
      snackbar.trigger("Time is up!", "error");

      const payload = JSON.parse(localStorage.getItem(ANSWERS_KEY) || "[]");
      const result = await submitAnswer(
        quizId,
        profile.value.studentId,
        payload
      );

      clearStorageForThisQuiz();

      const sid = result?.startId ?? result?.StartId;
      const inferred = inferReviewPinRequirement(result);
      const requiresPinReview =
        typeof inferred === "boolean"
          ? inferred
          : routeRequireReviewPin.value;
      if (sid) {
        router.push({
          path: `/quiz-finish`,
          query: {
            score: result?.Score,
            grade: result?.Grade,
            exp: result?.Exp,
            startId: sid,
            quizGuid: quizId,
            ...(route.query.quizTitle ? { quizTitle: route.query.quizTitle } : {}),
            ...(requiresPinReview ? { requireReviewPin: "1" } : {}),
          },
        });
      } else {
        router.push({
          path: `/quiz-finish`,
          query: {
            quizGuid: quizId,
            ...(route.query.quizTitle ? { quizTitle: route.query.quizTitle } : {}),
            ...(requiresPinReview ? { requireReviewPin: "1" } : {}),
          },
        });
      }
    }
  }, 1000);

  handleGetProfile();
});

onUnmounted(() => {
  clearInterval(timerInterval);
});
</script>

<style scoped>
/* Keep the bullet perfectly round, independent of parent line-height or flex sizing */
.answer-bullet {
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto; /* never stretch */
  aspect-ratio: 1 / 1; /* extra guard; width/height already enforce square */
  line-height: 0; /* prevent inheriting line-height from text */
  -webkit-tap-highlight-color: transparent;
  background: #fff;
}
.answer-bullet--on {
  background: #2563eb;
  color: #fff;
}
</style>
